import React, {useEffect, useState} from 'react';
import * as PropTypes from "prop-types";
import {withTranslation} from 'react-i18next';
import {compose} from 'redux';
import SearchComponent from "../SearchComponent/SearchComponent";
import ErrorRequestHandler from "../../ErrorRequestHandler";
import useRoutes from "../../../hooks/useRoutes";
import {Button} from '@mui/material';
import Alert from '@mui/material/Alert';
import {setSearchQuery, setSearchQueryTerm} from "../../../actions";
import ListComponent from "./ListComponent";
import {connect} from "react-redux";
import AlertComponent from "../../AlertComponent";
import RouteRegionComponent from "./RouteRegionComponent";
import RouteMapComponent from "./RouteMapComponent";
import Constants from "../../../config/constants";
import {useAuth} from "../../../context/auth-context";
import {flattenSearchResult} from "../../../utils/helpers";
import FillScreenLayout from "../../FillScreenLayout";
import {getUrl, PWAUrls} from "../../../config/urls";

const LIST_ITEM_HEIGHT = 106;
const LIST_HEADER_HEIGHT = 35;

const RouteListComponent = ({t, i18n, query: propQuery, facetDistribution, screenName, setSearchQuery, setSearchQueryTerm, guidebook}) => {
    const [query, setQuery] = useState(propQuery);
    const [expanded, setExpanded] = useState(false);
    const [orderRegions, setOrderRegions] = useState([]);
    const [regionIds, setRegionIds] = useState({});
    const auth = useAuth();
    const latitude = guidebook?.latitude ? guidebook.latitude : null;
    const longitude = guidebook?.longitude ? guidebook.longitude : null;
    const zoom = guidebook?.zoom ? guidebook.zoom : null;

    const orderList = [
        {value: Constants.RouteListComponent.queryOrder.region, label: t("SearchComponent.display.region")},
        {value: Constants.RouteListComponent.queryOrder.list, label: t("SearchComponent.display.list")},
        {value: Constants.RouteListComponent.queryOrder.map, label: t("SearchComponent.display.map")},
    ];

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('q') && urlParams.get('q') !== query.q) {
        setQuery({...query, q: urlParams.get('q')})
    }

    const {
        status,
        data,
        error,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isFetching } = useRoutes({...query,
        facetDistribution: facetDistribution.map(m => m.name),
        guidebookId: guidebook?.id,
    },  screenName, auth, i18n?.language);

    const switchExpanded = () => {
        setExpanded(!expanded);
    };

    // empty array on second argument = componentDidmount
    useEffect(() => {
        // returned function will be called on component unmount
        return () => {
            if (screenName === Constants.SCREEN_NAME.TOPO) {
                query.screenChoices = [];
                setSearchQuery(screenName, query);
            }
        }
    }, []);

    useEffect(() => {
        // if (import.meta.env.VITE_DEBUG !== undefined && import.meta.env.VITE_DEBUG) {
        //     console.log("update", query);
        // }
        if (query.order !== Constants.RouteListComponent.queryOrder.region && query.openRegionId) {
            setQuery({...query, openRegionId: null});
        }

        //setSearchQueryTerm(query.q);
        setSearchQuery(screenName, query);
    }, [query]);

    useEffect( () => {
        setQuery({...query, language: i18n.language});
    }, [i18n])

    const loadMore = () => {
        if (hasNextPage) {
            fetchNextPage();
        }
        // if (hasNextPage) {
        //     fetchNextPage({ pageParam: data.pageParams.length });
        // }
    };

    const handleClearChoices = () => {
        setOrderRegions([]);
        updateQuery({...query, choices: []});
    };

    const updateQuery = (query) => {
        setOrderRegions([]);
        setQuery(query);
    }

    const updateRegionQuery = (openRegionId) => {
        setQuery({...query, openRegionId: parseInt(openRegionId), offset: 0});
    }

    let hasRoute = true;
    let isEmptyResult = true;
    let facetsData = [];
    let facetRegion = {};
    let facetDistributionData = [];
    let nbHits = null;
    if (status === 'success') {
        isEmptyResult = data.pages[0].data.hits?.length === 0 && query.order !== Constants.RouteListComponent.queryOrder.map;
        nbHits = data.pages[0].data.nbHits;
        if (data.pages[0].data.hasOwnProperty('hasRoutes') && !data.pages[0].data.hasRoutes) {
            hasRoute = false;
        }

        facetsData = data.pages[0].data.facets;
        facetDistributionData = data.pages[0].data.facetDistribution;

        if (typeof facetsData !== "undefined") {
            facetRegion = facetsData.find(f => f.name === 'regionIds');
        }

        if (query.order === Constants.RouteListComponent.queryOrder.region
            && facetRegion?.treeList
            && orderRegions.length === 0 && (query.offset === 0 || !query.offset)) {
            setOrderRegions(facetRegion?.treeList);
            setRegionIds(facetDistributionData.regionIds);
        //     Autoopen first region
        //     if (!query.openRegionId || (query.openRegionId && !facetDistributionData.regionIds.hasOwnProperty(query.openRegionId))) {
        //         setQuery({...query, openRegionId: facetRegion?.treeList[0].id});
        //     }
        }
    }

    const flatItems = flattenSearchResult(data);
    const showRegionList = query.order === Constants.RouteListComponent.queryOrder.region && orderRegions.length > 0;

    if (!hasRoute) {
        return (
            <AlertComponent
                title={t("RouteListComponent.alert.title")}
                message={t("RouteListComponent.alert.empty")}
                severity={'info'}
                ctaLabel={t("RouteListComponent.alert.emptyCta")}
                ctaIcon={'search'}
                ctaLinkTo={getUrl(PWAUrls.SEARCH, {lng: i18n.language})}
                reload={false}
                backgroundIcon={'search'}
            />
        );
    }

    if (status === "error") {
        return <ErrorRequestHandler error={error}/>;
    }

    const itemCount = flatItems.length ? flatItems.length+1 : 0;
    const listComponentHeight = LIST_ITEM_HEIGHT*Math.min(8, itemCount);

    const showNoRouteCriterias = ((isEmptyResult && query.order !== Constants.RouteListComponent.queryOrder.region) ||
    (query.order === Constants.RouteListComponent.queryOrder.region && Object.keys(regionIds).length === 0));

    return (
        <FillScreenLayout>
            <div className={"mt-1" + (showNoRouteCriterias ? " borderBottom" : "") }>
                <SearchComponent
                    facetDistribution={facetDistribution}
                    facetDistributionData={facetDistributionData}
                    updateQuery={updateQuery}
                    switchExpanded={switchExpanded}
                    query={query}
                    facets={facetsData}
                    orderLabel={t("SearchComponent.display.label")}
                    orderList={orderList}
                    firstRegionId={Array.isArray(orderRegions) && orderRegions.length > 0 ? orderRegions[0].id : null}
                />
            </div>
            {((flatItems.length === 0 && status === 'pending') && !showRegionList) && <div className={"flexGrowAuto"}>
                <div style={{height:'100%'}}>
                    <div className={"loadingWrapper"}>
                        <div id="loading"/>
                    </div>
                </div>
            </div>}
            {showRegionList && orderRegions.length > 0 && <RouteRegionComponent
                screenName={screenName}
                nodes={orderRegions}
                updateRegionQuery={updateRegionQuery}
                regionIds={regionIds}
                openRegionId={query.openRegionId}
            >
                <div style={{height: listComponentHeight + 'px'}}>
                    {isFetching && !isFetchingNextPage && <div className={"loadingWrapper"}>
                        <div id="loading"/>
                    </div>}
                    {Array.isArray(flatItems) && flatItems.length > 0 && (!isFetching || isFetchingNextPage) && <ListComponent
                        items={flatItems}
                        nbHits={nbHits}
                        loadMore={loadMore}
                        hasNextPage={hasNextPage}
                        moreItemsLoading={isFetchingNextPage}
                        showPrice={!query.myroute}
                        screenName={screenName}
                    />}
                </div>
            </RouteRegionComponent>}

            {status === 'success' && <>
                {query.order === Constants.RouteListComponent.queryOrder.map && <RouteMapComponent
                    items={flatItems}
                    isFetching={isFetching}
                    screenName={screenName}
                    latitude={latitude}
                    longitude={longitude}
                    zoom={zoom}
                />}

                {!isEmptyResult && query.order === Constants.RouteListComponent.queryOrder.list && <div className={"flexGrowAuto"}>
                    <ListComponent
                        showPrice={!query.myroute}
                        items={flatItems}
                        nbHits={nbHits}
                        loadMore={loadMore}
                        hasNextPage={hasNextPage}
                        screenName={screenName}
                        isFetchingNextPage={isFetchingNextPage}
                    />
                </div>}

                {showNoRouteCriterias && <div className={"flexGrowAuto mx-2"}>
                    <Alert severity="info" style={{marginTop: "15px", marginBottom: "15px"}}>
                        {t("RouteListComponent.alert.emptySearch")}
                    </Alert>
                    <div className="text-center">
                        <Button variant="contained" color="primary" onClick={handleClearChoices}>{t("RouteListComponent.button.clearAll")}</Button>
                    </div>
                </div> }
            </>}
        </FillScreenLayout>
    );
};

const mapDispatchToProps = dispatch => {
    return {
        setSearchQuery: (screen, query) => {
            dispatch(setSearchQuery(screen, query))
        },
        setSearchQueryTerm: (term) => {
            if (term === '') {
                term = null;
            }
            dispatch(setSearchQueryTerm(term))
        }
    }
};

RouteListComponent.defaultProps = {
    screenName: PropTypes.string.isRequired,
    query: PropTypes.object.isRequired,
    facetDistribution: PropTypes.object.isRequired,
};

export default compose(
    withTranslation(),
    connect(
        null,
        mapDispatchToProps
    )
)(RouteListComponent)